import { effect, inject, Injectable, Injector, OnDestroy, signal } from '@angular/core';
import { Annotation } from '../pointcloud-utils/annotation';
import { SceneStateService } from '../services/scene-state.service';

export interface AnnotationData {
  uuid: string;
  title: string;
  description: string;
  position: { x: number; y: number };
  visible: boolean;
  highlighted: boolean;
  descriptionVisible: boolean;
  zIndex: number;
  opacity: number;
}

@Injectable({
  providedIn: 'root',
})
export class AnnotationService implements OnDestroy {
  public annotations = signal<AnnotationData[]>([]);
  private sceneState = inject(SceneStateService);
  private injector = inject(Injector);
  private viewer: any = null;
  private updateCallback: (() => void) | null = null;
  private cleanupEffects: (() => void)[] = [];

  constructor() {}

  /**
   * Initialize with the point cloud viewer.
   */
  initialize(viewer: any, updateCallback: () => void): void {
    try {
      this.viewer = viewer;
      this.updateCallback = updateCallback;

      const root: Annotation | undefined = viewer.scene?.annotations;
      if (root) {
        this.syncFromRoot(root);
        this.watchRoot(root);
      }
    } catch (_error) {
      // Error initializing annotations - allow the app to continue
    }
  }

  getTitle(uuid: string): string | undefined {
    const annotation = this.annotations().find(a => a.uuid === uuid);
    return annotation ? annotation.title : undefined;
  }

  getDescription(uuid: string): string | undefined {
    const annotation = this.annotations().find(a => a.uuid === uuid);
    return annotation ? annotation.description : undefined;
  }

  updateTitle(uuid: string, title: string): void {
    this.annotations.update(annotations =>
      annotations.map(a =>
        a.uuid === uuid
          ? {
              ...a,
              title: title,
            }
          : a,
      ),
    );
  }

  updateDescription(uuid: string, description: string): void {
    this.annotations.update(annotations =>
      annotations.map(a =>
        a.uuid === uuid
          ? {
              ...a,
              description: description,
            }
          : a,
      ),
    );
  }

  toggleAnnotationVisibility(uuid: string): void {
    this.annotations.update(annotations =>
      annotations.map(a =>
        a.uuid === uuid
          ? {
              ...a,
              visible: !a.visible,
            }
          : a,
      ),
    );

    this.notifyUpdate();
  }

  /**
   * Watch the root annotation's signals for child add/remove.
   */
  private watchRoot(root: Annotation): void {
    this.destroyEffects();

    const addRef = effect(
      () => {
        const added = root.childAdded();
        if (added) {
          this.addAnnotation(added);
        }
      },
      { injector: this.injector },
    );

    const removeRef = effect(
      () => {
        const removed = root.childRemoved();
        if (removed) {
          this.removeAnnotationByUuid(removed.uuid);
        }
      },
      { injector: this.injector },
    );

    this.cleanupEffects.push(() => addRef.destroy(), () => removeRef.destroy());
  }

  /**
   * Sync existing annotations from the root tree.
   */
  private syncFromRoot(root: Annotation): void {
    root.traverse((annotation: Annotation) => {
      if (annotation !== root) {
        this.addAnnotation(annotation);
      }
    });
  }

  private destroyEffects(): void {
    this.cleanupEffects.forEach(fn => fn());
    this.cleanupEffects = [];
  }

  ngOnDestroy(): void {
    this.destroyEffects();
    this.viewer = null;
  }

  /**
   * Add an annotation to the Angular array
   */
  private addAnnotation(annotation: Annotation): void {
    // Check if already exists
    const existingIndex = this.annotations().findIndex(a => a.uuid === annotation.uuid);

    if (existingIndex >= 0) return;

    // Convert to Angular annotation data
    const annotationData: AnnotationData = {
      uuid: annotation.uuid,
      title: annotation._title || 'Annotation',
      description: annotation._description || '',
      position: { x: 0, y: 0 }, // Will be updated in updatePositions
      visible: annotation._visible !== false,
      highlighted: annotation.isHighlighted || false,
      descriptionVisible: annotation.descriptionVisible || false,
      zIndex: 100,
      opacity: 0.5,
    };

    this.annotations.update(old => [...old, annotationData]);
    this.notifyUpdate();
  }

  private removeAnnotationByUuid(uuid: string): void {
    const index = this.annotations().findIndex(a => a.uuid === uuid);
    if (index >= 0) {
      this.annotations.update(old => old.filter(a => a.uuid !== uuid));
      this.notifyUpdate();
    }
  }

  /**
   * Update annotation positions from updateAnnotations calculation
   * This should be called from the viewer's update loop
   */
  updatePositions(viewer: any): void {
    const annotations = this.sceneState.annotations();
    if (!annotations) return;

    // Get renderer size
    const canvas = viewer.renderer.domElement;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    const camera = this.sceneState.getActiveCamera();
    if (!camera) return;

    // Create a map to track updates
    const updates = new Map<string, Partial<AnnotationData>>();

    annotations.traverse((annotation: any) => {
      if (annotation === annotations) return;

      const existingAnnotation = this.annotations().find(a => a.uuid === annotation.uuid);
      if (!existingAnnotation) return;

      // Calculate screen position (same logic as viewer.js updateAnnotations)
      const position = annotation.position
        ? annotation.position.clone()
        : annotation.boundingBox.getCenter({});

      if (annotation.offset) {
        position.x += annotation.offset.x;
        position.y += annotation.offset.y;
        position.z += annotation.offset.z;
      }

      // Apply camera projection
      const projected = this.projectToScreen(position, camera, width, height);
      if (
        projected.x == existingAnnotation.position.x &&
        projected.y == existingAnnotation.position.y
      ) {
        return;
      }

      // Check if annotation is visible (within screen bounds and in front of camera)
      const isInBounds = projected.z >= -1 && projected.z <= 1;

      // Calculate z-index based on distance
      const distance = camera.position.distanceTo(position);
      const zIndex = 10000000 - distance * (10000000 / camera.far);

      // Store the update for this annotation
      updates.set(annotation.uuid, {
        position: { x: projected.x, y: projected.y },
        visible: existingAnnotation.visible && isInBounds,
        descriptionVisible: existingAnnotation.descriptionVisible || false,
        zIndex: Math.floor(existingAnnotation.descriptionVisible ? zIndex + 10000000 : zIndex),
        opacity: existingAnnotation.opacity,
      });
    });

    // Apply all updates at once using signal update
    if (updates.size > 0) {
      this.annotations.update(annotations =>
        annotations.map(a => {
          const update = updates.get(a.uuid);
          return update ? { ...a, ...update } : a;
        }),
      );
    }

    this.notifyUpdate();
  }

  /**
   * Project 3D position to screen coordinates
   */
  private projectToScreen(
    position: any,
    camera: any,
    width: number,
    height: number,
  ): { x: number; y: number; z: number } {
    const screenPos = { x: 0, y: 0, z: 0 };

    // Use Three.js Vector3 project method
    if (position.project) {
      const projected = position.clone();
      projected.project(camera);

      screenPos.x = (width * (projected.x + 1)) / 2;
      screenPos.y = height * (1 - (projected.y + 1) / 2);
      screenPos.z = projected.z;
    }

    return screenPos;
  }

  /**
   * Toggle annotation description visibility
   */
  toggleAnnotationDescription(uuid: string): void {
    this.annotations.update(annotations =>
      annotations.map(a =>
        a.uuid === uuid
          ? {
              ...a,
              descriptionVisible: !a.descriptionVisible,
            }
          : a,
      ),
    );

    this.notifyUpdate();
  }

  /**
   * Get the current annotations array
   */
  getAnnotations(): AnnotationData[] {
    return this.annotations();
  }

  /**
   * Notify that annotations have been updated
   */
  private notifyUpdate(): void {
    if (this.updateCallback) {
      this.updateCallback();
    }
  }

  removeAnnotation(uuid: string): void {
    this.removeAnnotationByUuid(uuid);
  }
}
